// tos.ts
//
// The Terms of Service version, and the metadata a signup records to say the
// user accepted it (takenote_decisions.md 9.6, and the Auth - TOS FR).
//
// ---------------------------------------------------------------------------
// WHERE ACCEPTANCE IS STORED, AND WHY IT IS NOT A TABLE.
//
// In `auth.users.raw_user_meta_data`, which is part of the user row and is
// destroyed by the same DELETE that destroys the account (9.5). A
// `tos_acceptance` table keyed by user_id would have to OUTLIVE the account to
// audit anything, and 10.1 promises - in the requirements and on the TOS page
// this file names - that the username is the single thing kept. Adding a second
// exception in the phase that publishes that sentence would make it false in
// public. The FR's own acceptance criterion allows "via Supabase metadata",
// so this satisfies it as written.
//
// THE VERSION STRING IS DUPLICATED IN SQL, DELIBERATELY. The hook that enforces
// acceptance reads `public.current_tos_version()`
// (first made in supabase/migrations/20260909_230_phase10_tos_acceptance.sql, set for launch by 20261022_250_phase16_tos_version.sql), and the two
// are not shared because they run in different runtimes - a shared constant
// would let both be wrong together, which is the same reasoning noteLimits.ts
// records about MAX_CIPHERTEXT_BYTES. `pnpm e2e` asks Postgres for its version
// and greps this file for the same literal, so drift fails a test.
//
// If they disagree, every signup is refused with "The Terms of Service must be
// accepted" while the checkbox is ticked - loud, immediate, and impossible to
// mistake for anything else.
// ---------------------------------------------------------------------------

/** The version of the Terms a new account must accept. Bump SQL in the same commit. */
export const TOS_VERSION = "2026-10-22";

/**
 * Where the signup form's Terms link points.
 *
 * The words "Terms of Service" in the checkbox label are the link, and it opens
 * in a new tab (11.3) - a user who reads the Terms halfway through signing up
 * must not come back to an empty form.
 */
export const TOS_PATH = "/tos";

export type TosAcceptance = {
  tos_accepted: true;
  tos_version: string;
  tos_accepted_at: string;
};

/**
 * The acceptance record, for `supabase.auth.signUp({ options: { data } })`.
 *
 * `tos_accepted_at` is the browser's clock, so it is a claim the client makes
 * about itself and is recorded as one. The authoritative moment is
 * `auth.users.created_at`, which the server writes in the same statement that
 * stores this metadata - so for any signup that actually completed, the two
 * describe the same instant. The timestamp is kept because the FR asks for
 * "version + timestamp"; the version is the half the server validates.
 */
export function tosAcceptance(): TosAcceptance {
  return {
    tos_accepted: true,
    tos_version: TOS_VERSION,
    tos_accepted_at: new Date().toISOString(),
  };
}
